import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Loader2, Briefcase, Car, PoundSterling, TrendingDown, Wallet } from 'lucide-react';
import { formatGBP, formatDate, shortId } from '@/lib/utils';
import { toast } from 'sonner';

function StatRow({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className={`h-7 w-7 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-sm text-slate-700">{label}</span>
      </div>
      <span className="text-sm font-semibold text-slate-900">{value}</span>
    </div>
  );
}

export default function PartnerEarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get('/partner/earnings');
      setData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load earnings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !data) {
    return <p className="text-center text-sm text-slate-500 py-10"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading earnings...</p>;
  }

  return (
    <div className="space-y-4">
      <Card className="p-5 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
        <p className="text-xs text-slate-300 uppercase tracking-wider">Your total payout</p>
        <p className="text-3xl font-bold mt-1" data-testid="earnings-payout">{formatGBP(data.partner_payout)}</p>
        <p className="text-xs text-slate-300 mt-1">across {data.total_jobs} completed job{data.total_jobs === 1 ? '' : 's'}</p>
      </Card>

      <Card className="p-4 border-slate-200">
        <p className="text-sm font-semibold text-slate-900 mb-2">Breakdown</p>
        <StatRow testId="earn-jobs" icon={Briefcase} label="Completed jobs" value={data.total_jobs} accent="bg-slate-700" />
        <StatRow testId="earn-service" icon={PoundSterling} label="Total service value" value={formatGBP(data.total_service_value)} accent="bg-sky-600" />
        <StatRow testId="earn-travel" icon={Car} label="Total travel fees" value={formatGBP(data.total_travel_fees)} accent="bg-violet-600" />
        <StatRow testId="earn-revenue" icon={Wallet} label="Customer total" value={formatGBP(data.total_revenue)} accent="bg-emerald-600" />
        <StatRow testId="earn-commission" icon={TrendingDown} label="BeautyMateUK commission" value={formatGBP(data.platform_commission)} accent="bg-rose-600" />
      </Card>

      <Card className="p-4 border-slate-200">
        <p className="text-sm font-semibold text-slate-900 mb-2">Completed jobs</p>
        {data.bookings.length === 0 ? (
          <p className="text-xs text-slate-500 py-3 text-center">No completed jobs yet</p>
        ) : (
          <div className="space-y-2">
            {data.bookings.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-xs border border-slate-100 rounded-lg p-2" data-testid={`earn-job-${b.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{b.service_name || 'Service'}</p>
                  <p className="text-[11px] text-slate-500">#{shortId(b.id)} · {formatDate(b.updated_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold text-emerald-700">{formatGBP(b.partner_payout)}</p>
                  <p className="text-[10px] text-rose-500">- {formatGBP(b.platform_commission)} fee</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="text-[11px] text-center text-slate-400 px-4">
        Commission formula: 20% of service price goes to BeautyMateUK · You keep 80% of service price + 100% of travel fee.
      </p>
    </div>
  );
}
